import { createEntityStateFactory, EntityId, EntityStateFactory, IdSelector } from './EntityState';
import { createEntityStateUpdaters, EntityStateUpdaters } from './EntityStateUpdaters';
import {
  createEntityStateSelectorsFactory,
  EntityStateSelectorsFactory,
} from './EntityStateSelectors';

/**
 * Options for configuring an {@link EntityAdapter}
 */
export interface EntityAdapterOptions<T, Id extends EntityId> {
  selectId?: IdSelector<T, Id>;
}

/**
 * A collection of utils for creating, managing, and querying normalized entity state
 * in a structured manner.
 */
export interface EntityAdapter<T, Id extends EntityId>
  extends Required<EntityAdapterOptions<T, Id>>,
    EntityStateFactory<T, Id>,
    EntityStateSelectorsFactory<T, Id>,
    EntityStateUpdaters<T, Id> {}

/**
 * Creates an {@link EntityAdapter} for managing a collection of entities.
 */
export const createEntityAdapter = <T, Id extends EntityId>(
  options: EntityAdapterOptions<T, Id> = {},
): EntityAdapter<T, Id> => {
  const { selectId = (entity: any) => entity.id } = options;
  const stateFactory = createEntityStateFactory<T, Id>();
  const selectorsFactory = createEntityStateSelectorsFactory<T, Id>();
  const stateUpdaters = createEntityStateUpdaters<T, Id>({ selectId, ...stateFactory });

  return {
    selectId,
    ...stateFactory,
    ...selectorsFactory,
    ...stateUpdaters,
  };
};
